import React from 'react';
import axios from "axios";
import { useNavigate } from 'react-router-dom';

import { Container, Grid, useMantineTheme, Box, Group, Flex } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconFilter, IconReload } from '@tabler/icons-react';

import { useDisplayModeContext } from "../../context/displayModeContext";
import { useLoginContext } from "../../context/loginContext";

import { BE_API_TWO, utils, ROOT_BASE_URL } from "../../utils/globalVariables";
import { regexNoStriped, isoDateToSqlDate, getPropInSubLevelRole, safetyDecodeJWT } from "../../utils/globalFunctions";

import AmountOfData from "../../components/general/AmountOfData";
import DisplayModeSelector from "../../components/general/DisplayModeSelector";
import ActionIconComponent from "../../components/general/ActionIconComponent";
import ImageLightBox2 from "../../components/general/ImageLightBox2";

import NoData from "../../components/general/NoData";
import WrapperUtils from "../../layout/WrapperUtils";

import GaransiStep2Card from "./GaransiStep2Card";
import GaransiStep2Table from "./GaransiStep2Table";
import GaransiStep2Selected from "./GaransiStep2Selected";
import GaransiStep2Filter from "./GaransiStep2Filter";
import SearchInputComponent from "../../components/general/SearchInputComponent";

const GaransiStep2Index = ({
    stateMenu,
    stateSubMenu,
    stateTab,
}) => {

    const mountedRef = React.useRef(true);
    const navigate = useNavigate();

    const theme = useMantineTheme();
    const matchesMinMD = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
    const { displayMode } = useDisplayModeContext();
    const { loginContext, handleSetLoginContext } = useLoginContext();

    const notificationId = React.useRef({
        errorGetData: 'garansiStep2Index-1',
        successSubmitTerima: 'garansiStep2Index-2',
    })

    const filterDefaultRef = React.useRef({
        tanggalPengajuanAwal: null,
        tanggalPengajuanAkhir: null,

        tanggalKirimAwal: null,
        tanggalKirimAkhir: null,
    })

    const sortList = {
        categoryName: {
            slug: 'stock_category_name',
            caption: 'Kategori',
            type: utils.sortType.string.slug
        },
        categorySubName: {
            slug: 'stock_category_sub_name',
            caption: 'Sub Kategori',
            type: utils.sortType.string.slug
        },
        supplierName: {
            slug: 'supplier_name',
            caption: 'Supplier',
            type: utils.sortType.string.slug
        },
        garansiJumlah: {
            slug: 'jumlah_detil',
            caption: 'Jumlah',
            type: utils.sortType.integer.slug
        },
        tanggalPengajuan: {
            slug: 'stock_garansi_tanggal_pengajuan',
            caption: 'Tanggal Pengajuan',
            type: utils.sortType.date.slug
        },
        teknisiPengaju: {
            slug: 'stock_garansi_pengaju_user_name',
            caption: 'Diajukan Oleh',
            type: utils.sortType.string.slug
        },

        tanggalKirimSupplier: {
            slug: 'tanggal_kirim_ke_supplier',
            caption: 'Tanggal Kirim Ke Supplier',
            type: utils.sortType.date.slug
        },
        userPengirim: {
            slug: 'stock_garansi_pengirim_user_name',
            caption: 'Dikirim Oleh',
            type: utils.sortType.string.slug
        },
    }

    const [data, setData] = React.useState({
        data: [],
        dataCount: 0,
        dataFlag: 'idle',
        dataLoading: false,
        dataRefresh: new Date(),
        dataOffset: 0,
        dataSortSelected: sortList.categoryName,
        dataSearchInputForBE: '',

        imageLightboxImages: '',
        imageLightboxTitle: '',
        imageLightboxIndex: '',
        imageLightboxFieldName: '',
        imageLightboxShow: false,

        filterData: {
            drawer: false,
            tanggalPengajuanAwal: filterDefaultRef.current.tanggalPengajuanAwal,
            tanggalPengajuanAkhir: filterDefaultRef.current.tanggalPengajuanAkhir,

            tanggalKirimAwal: filterDefaultRef.current.tanggalKirimAwal,
            tanggalKirimAkhir: filterDefaultRef.current.tanggalKirimAkhir,
        },

        selectedTrigger: new Date(),
    });

    React.useEffect(() => {
        return () => {
            mountedRef.current = false;
        }
    }, [])

    const fnSort = (stateSortSelected) => {
        setData(d => ({
            ...d,
            dataLoading: true,
            dataSortSelected: {
                ...d.dataSortSelected,
                slug: d.dataSortSelected.slug[0] === '-' ? stateSortSelected.slug : '-' + stateSortSelected.slug,
                caption: stateSortSelected.caption,
                type: stateSortSelected.type
            }
        }));
    };

    const AmountOfDataComponent = () => {
        return (
            <AmountOfData
                stateDataLimit={utils.dataLimit}
                stateDataOffset={data.dataOffset}
                stateAmountDataAll={data.dataCount}
                stateDataFlag={data.dataFlag}
                stateSuffix=''

                fnFirstData={(stateNewOffset) => setData(d => ({
                    ...d,
                    dataLoading: true,
                    dataRefresh: new Date(),
                    dataOffset: stateNewOffset
                }))}

                fnLastData={(stateNewOffset) => setData(d => ({
                    ...d,
                    dataLoading: true,
                    dataRefresh: new Date(),
                    dataOffset: stateNewOffset
                }))}

                fnPreviousData={(stateNewOffset) => setData(d => ({
                    ...d,
                    dataLoading: true,
                    dataRefresh: new Date(),
                    dataOffset: stateNewOffset
                }))}

                fnNextData={(stateNewOffset) => setData(d => ({
                    ...d,
                    dataLoading: true,
                    dataRefresh: new Date(),
                    dataOffset: stateNewOffset
                }))}
            />
        )
    }

    React.useEffect(() => {
        setData(d => ({ ...d, dataLoading: true }));

        const formDT = new FormData();
        const formDTHeader = { 'Content-Type': 'application/json' }

        formDT.append('json_token', loginContext.syswebapp);
        formDT.append('data_limit', utils.dataLimit);
        formDT.append('data_offset', data.dataOffset);
        formDT.append('order_by', regexNoStriped(data.dataSortSelected.slug));
        formDT.append('order_type', data.dataSortSelected.slug[0] === '-' ? 'asc' : 'desc');

        formDT.append('flag_all', utils.flagAll);
        formDT.append('filter_search', data.dataSearchInputForBE);

        formDT.append('tanggal_pengajuan_awal', data.filterData.tanggalPengajuanAwal === null ? utils.flagAll : isoDateToSqlDate(data.filterData.tanggalPengajuanAwal, ''));
        formDT.append('tanggal_pengajuan_akhir', data.filterData.tanggalPengajuanAkhir === null ? utils.flagAll : isoDateToSqlDate(data.filterData.tanggalPengajuanAkhir, ''));

        formDT.append('tanggal_kirim_awal', data.filterData.tanggalKirimAwal === null ? utils.flagAll : isoDateToSqlDate(data.filterData.tanggalKirimAwal, ''));
        formDT.append('tanggal_kirim_akhir', data.filterData.tanggalKirimAkhir === null ? utils.flagAll : isoDateToSqlDate(data.filterData.tanggalKirimAkhir, ''));

        formDT.append('garansi_item_status', utils.statusItemGaransi.sudah_dikirim_ke_supplier.value);

        axios({
            method: 'post',
            url: BE_API_TWO + '/klaim_garansi',
            data: formDT,
            headers: formDTHeader,
        }).then((response) => {
            if (!mountedRef.current) return null;

            setData(d => ({ ...d, dataLoading: false }));

            if (response.data.flag_status === 'isset') {
                setData(d => ({
                    ...d,
                    data: response.data.data,
                    dataCount: response.data.data_count,
                    dataFlag: response.data.data.length > 0 ? true : false,
                }));
            }

            if (response.data.flag_status === 'invalid-json-token') {
                handleSetLoginContext(null);
            }

            if (response.data.flag_status === 'user-inactive') {
                handleSetLoginContext(null);
            }
        }).catch(error => {
            if (!mountedRef.current) return null;

            setData(d => ({
                ...d,
                dataLoading: false,
            }));

            notifications.show({
                id: notificationId.current.errorGetData,
                title: utils.notification.warning.caption,
                message: error.message,
                color: utils.notification.warning.color,
                icon: utils.notification.warning.icon,
                withBorder: true,
                autoClose: true,
            })
        })
    }, [handleSetLoginContext, loginContext.syswebapp, data.dataRefresh, data.dataSearchInputForBE, data.dataSortSelected.slug, data.dataOffset, data.filterData.tanggalPengajuanAwal, data.filterData.tanggalPengajuanAkhir, data.filterData.tanggalKirimAwal, data.filterData.tanggalKirimAkhir])

    return (
        <React.Fragment>
            <WrapperUtils
                stateDataLoading={data.dataLoading}
            >
                <Group spacing='xs' noWrap>
                    <ActionIconComponent
                        stateVariant='light'
                        stateRadius='xl'
                        stateTitle='Filter'
                        stateSize='lg'
                        fnOnClick={() => setData(d => ({
                            ...d,
                            filterData: {
                                ...d.filterData,
                                drawer: true
                            }
                        }))}

                        StateIcon={IconFilter}
                        stateIconSize='1.125rem'
                        stateIconStrokeWidth={1.25}
                        stateIconStyle={{
                            color: theme.colorScheme === 'light' ? '#000' : theme.colors.gray[0]
                        }}
                    />

                    <DisplayModeSelector />

                    <ActionIconComponent
                        stateVariant='light'
                        stateRadius='xl'
                        stateTitle='Reload Data'
                        stateSize='lg'
                        fnOnClick={() => setData(d => ({ ...d, dataLoading: true, dataRefresh: new Date() }))}

                        StateIcon={IconReload}
                        stateIconSize='1.125rem'
                        stateIconStrokeWidth={1.25}
                        stateIconStyle={{
                            color: theme.colorScheme === 'light' ? '#000' : theme.colors.gray[0]
                        }}
                    />

                    <SearchInputComponent
                        statePlaceHolder='Search Garansi'
                        fnDoSearch={(val) => setData(d => ({
                            ...d,
                            dataOffset: 0,
                            dataSearchInputForBE: val,
                            dataRefresh: new Date(),
                        }))}
                    />
                </Group>

                {matchesMinMD &&
                    <Flex
                        sx={{ flex: 1 }}
                        justify="flex-end"
                        align="center"
                        direction="row"
                        wrap="nowrap"
                    >
                        <AmountOfDataComponent />
                    </Flex>
                }
            </WrapperUtils>

            <Container fluid>
                <Grid my={0}>
                    {data.data.length <= 0 && data.dataFlag !== 'idle' &&
                        <Grid.Col span={12}>
                            <NoData />
                        </Grid.Col>
                    }

                    {(data.data.length > 0) && (displayMode === utils.displayMode.card.slug) &&
                        <Grid.Col
                            span={12}
                            py={0}
                        >
                            <Box mt='md'>
                                <GaransiStep2Card
                                    stateData={data.data}
                                    stateSortList={sortList}
                                    stateSortSelected={data.dataSortSelected}
                                    fnSort={(val) => {
                                        fnSort(val);
                                    }}

                                    fnSelectedData={(val) => {
                                        navigate(`/${getPropInSubLevelRole(safetyDecodeJWT(loginContext, 'syswebapp', 'syswebappsl'), 'slugLink', '')}/${stateMenu}/${stateSubMenu}/${stateTab}/${val.stock_garansi_id}`, { replace: true });

                                        setData(d => ({ ...d, dataRefresh: new Date(), dataLoading: true, selectedTrigger: new Date() }));
                                        // ! apakah ini perlu ? perlu update index sewaktu selected ?
                                    }}
                                />
                            </Box>
                        </Grid.Col>
                    }

                    {(data.data.length > 0) && (displayMode === utils.displayMode.table.slug) &&
                        <Grid.Col
                            span={12}
                            py={0}
                        >
                            <Box mt='md'>
                                <GaransiStep2Table
                                    stateDataOffset={data.dataOffset}
                                    stateData={data.data}
                                    stateSortList={sortList}
                                    stateSortSelected={data.dataSortSelected}
                                    fnSort={(val) => {
                                        fnSort(val);
                                    }}

                                    fnSelectedData={(val) => {
                                        navigate(`/${getPropInSubLevelRole(safetyDecodeJWT(loginContext, 'syswebapp', 'syswebappsl'), 'slugLink', '')}/${stateMenu}/${stateSubMenu}/${stateTab}/${val.stock_garansi_id}`, { replace: true });

                                        setData(d => ({ ...d, dataRefresh: new Date(), dataLoading: true, selectedTrigger: new Date() }));
                                        // ! apakah ini perlu ? perlu update index sewaktu selected ?
                                    }}
                                />
                            </Box>
                        </Grid.Col>
                    }

                    {data.data.length > 0 &&
                        <Grid.Col span={12} className='amountOfDataBottom'>
                            <Group mt='xs' mb='md' position='apart'>
                                <Box>&nbsp;</Box>

                                <AmountOfDataComponent />
                            </Group>
                        </Grid.Col>
                    }
                </Grid>
            </Container>

            <GaransiStep2Filter
                stateShow={data.filterData.drawer}
                fnShow={() => setData(d => ({
                    ...d,
                    filterData: {
                        ...d.filterData,
                        drawer: false
                    }
                }))}

                stateDefaultFilter={{
                    ...filterDefaultRef.current,
                }}

                stateFilter={{
                    tanggalPengajuanAwal: data.filterData.tanggalPengajuanAwal,
                    tanggalPengajuanAkhir: data.filterData.tanggalPengajuanAkhir,

                    tanggalKirimAwal: data.filterData.tanggalKirimAwal,
                    tanggalKirimAkhir: data.filterData.tanggalKirimAkhir,
                }}

                fnFilter={(tglPengajuanAwal, tglPengajuanAkhir, tglKirimAwal, tglKirimAkhir) => {
                    setData(d => ({
                        ...d,
                        filterData: {
                            ...d.filterData,
                            tanggalPengajuanAwal: tglPengajuanAwal,
                            tanggalPengajuanAkhir: tglPengajuanAkhir,

                            tanggalKirimAwal: tglKirimAwal,
                            tanggalKirimAkhir: tglKirimAkhir,

                            drawer: false
                        },

                        dataRefresh: new Date(),
                        dataLoading: true,
                    }))
                }}
            />

            <GaransiStep2Selected
                stateData={{
                    stateMenu: stateMenu,
                    stateSubMenu: stateSubMenu,
                    stateTab: stateTab,
                    triggerUpdate: data.selectedTrigger
                }}

                fnSuccessEdit={() => {
                    setData(d => ({
                        ...d,
                        dataRefresh: new Date(),
                        dataLoading: true,
                    }));

                    notifications.show({
                        id: notificationId.current.successSubmitTerima,
                        title: utils.notification.success.caption,
                        message: 'Berhasil submit data.',
                        color: utils.notification.success.color,
                        icon: utils.notification.success.icon,
                        withBorder: true,
                        autoClose: true,
                    })
                }}

                fnClickPhoto={(object) => setData(d => ({
                    ...d,
                    imageLightboxImages: object.images,
                    imageLightboxTitle: object.title,
                    imageLightboxIndex: object.index,
                    imageLightboxFieldName: object.fieldName,
                    imageLightboxShow: object.show,
                }))}
            />

            <ImageLightBox2
                stateImages={Array.isArray(data.imageLightboxImages) && (data.imageLightboxImages.length > 0) ? data.imageLightboxImages.map(key => ROOT_BASE_URL + key[data.imageLightboxFieldName]) : []}

                stateTitle={data.imageLightboxTitle}
                stateSelectedIndex={data.imageLightboxIndex}
                stateShow={data.imageLightboxShow}
                fnShow={() => setData(d => ({ ...d, imageLightboxShow: false }))}
            />
        </React.Fragment>
    )
}

export default GaransiStep2Index;